<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, reactive, watch, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import SmmGroupGrowth from './SmmGroupGrowth.vue';
import SmmOrderRows from './SmmOrderRows.vue';
const props = defineProps({
  accountId: { type: String, required: true },
  groups: { type: Array, default: () => [] },
  selectedGroups: { type: Array, default: () => [] },
  from: { type: String, default: '' },
  to: { type: String, default: '' },
  disabled: Boolean,
});
const emit = defineEmits(['changed']);
const route = useRoute();
const router = useRouter();
const expanded = computed(() => route.query.smm === '1');
const period = ref(false);
const review = ref(false);
const page = ref(1);
const result = ref(null);
const loading = ref(false);
const saving = ref(false);
const uncertain = ref(false);
const denied = ref(false);
const error = ref('');
const formError = ref('');
const notice = ref('');
const dialog = ref(null);
const target = ref(null);
const form = reactive({
  numbers: '',
  group: '',
  date: '',
  reason: '',
  adopt: '',
  separate: false,
});
const options = computed(() =>
  props.groups.map(g => ({ value: String(g.id), label: g.title }))
);
let generation = 0;
let requests = new AbortController();
let pending = null;
const api = () => `/api/v1/accounts/${props.accountId}/tg_finance/smm_orders`;
const messages = {
  invalid_group: 'Выберите группу.',
  invalid_date: 'Проверьте дату заказа.',
  future_date: 'Дата заказа не может быть в будущем.',
  smm_order_number_invalid: 'Укажите только номера заказов цифрами.',
  smm_batch_invalid: 'Добавьте от 1 до 50 номеров.',
  smm_order_already_registered:
    'Этот номер уже добавлен. Откройте существующий заказ.',
  smm_number_already_set:
    'Номер уже подтверждён. Для другого заказа создайте отдельную запись.',
  smm_booked_date_required: 'Для учтённого расхода дата обязательна.',
  smm_manual_expense_invalid: 'Не найдена подходящая ручная операция расхода.',
  smm_adopt_single_only: 'Связать ручной расход можно только с одним заказом.',
  invalid_text: 'Укажите причину изменения.',
};
function failure(e) {
  return (
    messages[e?.response?.data?.error] ||
    'Не удалось выполнить запрос. Повторите попытку; расходы при ошибке не обнуляются.'
  );
}
async function load() {
  if (!expanded.value) return;
  generation += 1;
  const current = generation;
  loading.value = true;
  error.value = '';
  denied.value = false;
  try {
    const { data } = await axios({
      method: 'get',
      url: api(),
      params: {
        from: props.from,
        to: props.to,
        groups: props.selectedGroups.join(','),
        page: page.value,
        smm_period: period.value ? '1' : '0',
        smm_review: review.value ? '1' : '0',
      },
      signal: requests.signal,
    });
    if (current === generation) result.value = data;
  } catch (e) {
    if (current === generation) {
      denied.value = e?.response?.status === 403;
      error.value = failure(e);
    }
  } finally {
    if (current === generation) loading.value = false;
  }
}
function open(row = null) {
  if (saving.value || props.disabled) return;
  if (uncertain.value) {
    dialog.value?.open();
    return;
  }
  target.value = row;
  Object.assign(form, {
    numbers: row?.provider_order_id || '',
    group: String(row?.group_entry_id || props.selectedGroups[0] || ''),
    date: row?.ordered_on || '',
    reason: '',
    adopt: '',
    separate: row?.separate_expense || false,
  });
  pending = null;
  formError.value = '';
  dialog.value?.open();
}
function close() {
  if (!uncertain.value && !saving.value) {
    pending = null;
    formError.value = '';
  }
}
async function submit() {
  if (saving.value || props.disabled) return;
  if (!pending) {
    const numbers = [
      ...new Set(
        form.numbers
          .trim()
          .split(/[\s,;]+/)
          .filter(Boolean)
      ),
    ];
    if (!target.value && (!numbers.length || numbers.length > 50)) {
      formError.value = messages.smm_batch_invalid;
      return;
    }
    if (numbers.some(n => !/^[1-9]\d{0,23}$/.test(n))) {
      formError.value = messages.smm_order_number_invalid;
      return;
    }
    if (!form.group) {
      formError.value = messages.invalid_group;
      return;
    }
    if (target.value && !form.reason.trim()) {
      formError.value = messages.invalid_text;
      return;
    }
    const data = {
      request_key: crypto.randomUUID(),
      group_entry_id: Number(form.group),
      ordered_on: form.date || null,
    };
    if (form.adopt) {
      if (!/^[1-9]\d{0,14}$/.test(form.adopt)) {
        formError.value = messages.smm_manual_expense_invalid;
        return;
      }
      data.adopt_entry_id = Number(form.adopt);
    }
    if (target.value) {
      data.reason = form.reason;
      data.separate_expense = form.separate;
      if (!target.value.provider_order_id && numbers[0])
        data.provider_order_id = numbers[0];
    } else data.order_ids = numbers;
    pending = {
      account: props.accountId,
      url: target.value ? `${api()}/${target.value.id}` : api(),
      data,
    };
  }
  const current = props.accountId;
  saving.value = true;
  formError.value = '';
  try {
    await axios({
      method: 'post',
      url: pending.url,
      data: pending.data,
      signal: requests.signal,
    });
    if (current !== props.accountId) return;
    uncertain.value = false;
    pending = null;
    dialog.value?.close();
    notice.value =
      'Заказ сохранён. Стоимость будет проверена по API автоматически.';
    await load();
    emit('changed');
  } catch (e) {
    if (current !== props.accountId) return;
    uncertain.value = !e?.response || e.response.status >= 500;
    formError.value = uncertain.value
      ? 'Ответ не получен. Нажмите «Проверить сохранение»: повтор использует тот же номер операции.'
      : failure(e);
    if (!uncertain.value) pending = null;
  } finally {
    if (current === props.accountId) saving.value = false;
  }
}
async function refresh() {
  if (saving.value || props.disabled || uncertain.value) return;
  const current = props.accountId;
  saving.value = true;
  error.value = '';
  notice.value = '';
  try {
    const { data } = await axios({
      method: 'post',
      url: `${api()}/refresh`,
      data: {},
      signal: requests.signal,
    });
    if (current !== props.accountId) return;
    notice.value = data.refreshed
      ? 'Проверка завершена. Недоступные заказы и возвраты отмечены для сверки.'
      : 'Проверка пока не выполнена: проверьте подключение, подождите минуту и повторите.';
    await load();
    emit('changed');
  } catch (e) {
    if (current === props.accountId) error.value = failure(e);
  } finally {
    if (current === props.accountId) saving.value = false;
  }
}
watch(
  () => [
    props.accountId,
    props.from,
    props.to,
    props.selectedGroups.join(','),
    expanded.value,
    period.value,
    review.value,
    page.value,
  ],
  load,
  { immediate: true }
);
watch(
  () => props.accountId,
  () => {
    generation += 1;
    requests.abort();
    requests = new AbortController();
    pending = null;
    result.value = null;
    uncertain.value = false;
    saving.value = false;
    notice.value = '';
    dialog.value?.close();
    load();
  },
  { flush: 'sync' }
);
onBeforeUnmount(() => {
  generation += 1;
  requests.abort();
  pending = null;
});
</script>

<template>
  <section
    class="border border-n-weak rounded-xl p-4 min-w-0 flex flex-col gap-4"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="text-lg font-medium text-n-slate-12 m-0">Заказы SexySMM</h2>
      <Button
        :label="expanded ? 'Скрыть заказы' : 'Показать заказы'"
        slate
        faded
        :aria-expanded="expanded"
        @click="
          router.replace({
            query: { ...route.query, smm: expanded ? undefined : '1' },
          })
        "
      />
    </div>
    <p class="text-sm text-n-slate-11 m-0">
      Расходы по завершённым заказам привязаны к группам и дате заказа.
      Пополнение баланса учитывается переводом, стоимость услуг — расходом.
    </p>
    <template v-if="expanded">
      <Banner color="slate">
        Проверяются только добавленные номера, до 50 за раз. История сайта не
        загружается целиком. Частичные и отменённые заказы требуют сверки
        возврата; ошибки не обнуляют учтённую стоимость.
      </Banner>
      <div class="flex flex-wrap gap-2">
        <Button
          label="Добавить номера заказов"
          :disabled="disabled || saving || uncertain || denied"
          @click="open()"
        />
        <Button
          label="Обновить заказы"
          slate
          faded
          :is-loading="saving"
          :disabled="
            disabled || saving || uncertain || denied || !result?.configured
          "
          @click="refresh"
        />
        <Button
          label="Обновить список"
          slate
          ghost
          :disabled="loading || saving"
          @click="load"
        />
      </div>
      <div class="flex flex-wrap gap-4 text-sm text-n-slate-12">
        <label class="flex gap-2 items-center"
          ><input v-model="period" type="checkbox" @change="page = 1" />Только
          выбранный период</label
        >
        <label class="flex gap-2 items-center"
          ><input v-model="review" type="checkbox" @change="page = 1" />Только
          на сверке</label
        >
      </div>
      <p class="text-sm text-n-slate-11 m-0">
        {{ period ? 'Выбранный период' : 'Весь срок' }} · выбранные группы ·
        суммы RUB. Автопроверка новых и незавершённых заказов — каждые 15 минут,
        завершённых — раз в сутки.
      </p>
      <Button
        v-if="uncertain"
        label="Проверить сохранение"
        @click="dialog?.open()"
      />
      <Banner v-if="notice" color="green" role="status">{{ notice }}</Banner>
      <Banner v-if="denied" color="ruby" role="alert">
        Доступ разрешён только администратору.
      </Banner>
      <Banner v-else-if="error" color="ruby" role="alert">{{ error }}</Banner>
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        Загрузка заказов…
      </p>
      <template v-else-if="!error && result">
        <Banner v-if="!result.configured" color="amber">
          Подключите SexySMM в блоке баланса ниже. Сохранённые расходы остаются
          в учёте.
        </Banner>
        <Banner
          v-if="
            result.attention.review ||
            result.attention.errors ||
            result.attention.stale
          "
          color="amber"
        >
          На сверке: {{ result.attention.review }}. Ошибки проверки:
          {{ result.attention.errors }}. Нет свежей проверки:
          {{ result.attention.stale }}. До сверки доходность может быть
          неполной.
        </Banner>
        <p class="text-sm text-n-slate-11 m-0">
          Выполнение рассчитано по заказанному количеству и остатку SexySMM. Это
          отчёт сервиса, а не число сохранившихся участников. Цена подписчика
          появляется после подтверждения стоимости и при выполненном количестве
          больше нуля.
        </p>
        <SmmOrderRows
          v-if="result.items.length"
          :rows="result.items"
          :disabled="disabled || saving || uncertain"
          @edit="open"
        />
        <p v-else class="text-sm text-n-slate-11">
          Заказов в этой выборке нет. Добавьте номера из кабинета SexySMM или
          измените фильтры.
        </p>
        <div class="flex flex-wrap gap-3 items-center">
          <Button
            label="Назад"
            slate
            ghost
            :disabled="page <= 1"
            @click="page--"
          /><span class="text-sm text-n-slate-11"
            >{{ result.page }} / {{ result.pages }} · всего
            {{ result.total }}</span
          ><Button
            label="Далее"
            slate
            ghost
            :disabled="page >= result.pages"
            @click="page++"
          />
        </div>
      </template>
      <SmmGroupGrowth
        v-if="result && !loading && !error"
        :rows="result.group_members || []"
      />
    </template>
    <Dialog
      ref="dialog"
      :title="target ? 'Привязка заказа SexySMM' : 'Номера заказов SexySMM'"
      :confirm-button-label="uncertain ? 'Проверить сохранение' : 'Сохранить'"
      cancel-button-label="Закрыть"
      overflow-y-auto
      :is-loading="saving"
      @confirm="submit"
      @close="close"
    >
      <fieldset
        :disabled="saving || uncertain"
        class="border-0 p-0 m-0 flex flex-col gap-4 min-w-0"
      >
        <TextArea
          v-if="!target || !target.provider_order_id"
          id="smm-order-numbers"
          v-model="form.numbers"
          label="Номера заказов"
          :max-length="1500"
        />
        <p v-else class="text-sm text-n-slate-12">
          Заказ №{{ target.provider_order_id }}
        </p>
        <span class="text-sm text-n-slate-12">Группа</span
        ><ComboBox
          v-model="form.group"
          :options="options"
          placeholder="Выберите группу"
          aria-label="Группа заказа"
        />
        <Input
          v-model="form.date"
          type="date"
          label="Дата заказа — если известна"
        />
        <p class="text-sm text-n-slate-11 m-0">
          Без даты расход останется на сверке. Дата создания старого заказа не
          подтверждает дату банковского списания.
        </p>
        <Input
          v-model="form.adopt"
          label="Номер уже внесённого ручного расхода — если есть"
          inputmode="numeric"
        />
        <p class="text-sm text-n-slate-11 m-0">
          После точного совпадения группы, даты, счёта SexySMM и суммы ручной
          расход будет заменён автоматическим без удвоения.
        </p>
        <template v-if="target">
          <label class="flex gap-2 items-start text-sm text-n-slate-12"
            ><input v-model="form.separate" type="checkbox" />Подтверждаю:
            похожий ручной расход относится к другой услуге</label
          ><TextArea
            id="smm-order-reason"
            v-model="form.reason"
            label="Причина изменения / результат сверки"
            :max-length="500"
          />
        </template>
      </fieldset>
      <Banner v-if="formError" color="ruby" role="alert">
        {{ formError }}
      </Banner>
    </Dialog>
  </section>
</template>
